import { useEffect, useState } from "react";
import { useSEO } from "@/hooks/useSEO";
import { useNavigate } from "react-router-dom";
import { Gift, Sparkles, Zap, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { api, money } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Skeleton } from "@/components/ui/skeleton";

export const MYSTERY_IMG = "https://static.prod-images.emergentagent.com/jobs/6f6dbf2f-6597-4443-ae81-030622ef710b/images/214c1ccb9138b8f569bdf8abcae26a90b38ae85f33f40b91523835e8cdccf398.jpeg";

const BoxCard = ({ box, index }) => {
  const { add, setOpen } = useCart();
  const navigate = useNavigate();
  const out = box.stock <= 0;
  const featured = box.codes === 100;
  return (
    <div
      data-testid={`mystery-box-card-${box.codes}`}
      className={`rise relative rounded-xl border bg-[#0F1117] overflow-hidden flex flex-col transition-[border-color,box-shadow] duration-300 hover:shadow-[0_0_30px_rgba(16,185,129,0.18)] ${featured ? "border-emerald-500/60" : "border-white/10 hover:border-emerald-500/50"}`}
      style={{ animationDelay: `${index * 70}ms` }}
    >
      {featured && <span className="absolute top-3 right-3 z-10 label-eyebrow bg-emerald-500 text-[#08090C] px-2 py-1 rounded">Popular</span>}
      <div className="relative aspect-square overflow-hidden">
        <img src={box.image_url || MYSTERY_IMG} alt={box.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F1117] via-transparent to-transparent" />
        <p className="absolute bottom-3 left-5 font-mono font-bold text-5xl tracking-tighter">{box.codes}<span className="text-base text-gray-300 font-sans font-semibold ml-2">codes</span></p>
      </div>
      <div className="p-5 space-y-4 flex-1 flex flex-col">
        <p className="text-sm text-gray-400 flex-1">{box.description}</p>
        <div className="flex items-end justify-between">
          <div>
            <p data-testid={`mystery-box-price-${box.codes}`} className="font-mono font-bold text-2xl text-emerald-400">{money(box.price)}</p>
            <p className="font-mono text-xs text-gray-500">{money(box.price / box.codes)} per code</p>
          </div>
          {out && <span className="text-xs text-red-400">Sold out</span>}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button data-testid={`mystery-box-buy-${box.codes}`} disabled={out} onClick={() => { add(box); navigate("/checkout"); }}
            className="h-10 rounded-md bg-emerald-500 text-[#08090C] font-bold text-sm inline-flex items-center justify-center gap-1 hover:bg-emerald-400 disabled:opacity-40 transition-colors"><Zap size={14} /> Buy</button>
          <button data-testid={`mystery-box-add-${box.codes}`} disabled={out}
            onClick={() => { add(box); toast.success(`${box.name} added`, { action: { label: "View cart", onClick: () => setOpen(true) } }); }}
            className="h-10 rounded-md border border-white/15 text-sm font-semibold inline-flex items-center justify-center gap-1 hover:border-emerald-500/60 disabled:opacity-40 transition-colors"><ShoppingBag size={14} /> Add</button>
        </div>
      </div>
    </div>
  );
};

export default function MysteryBox() {
  useSEO({ title: "Mystery Box — Random Game Keys from $2 Each", description: "Blind boxes of 10 to 1,000 random Steam game keys at $2 per code. Every code is worth $5+, some unlock $50–$100 games. Pay with USDT.", path: "/mystery" });
  const [boxes, setBoxes] = useState(null);
  useEffect(() => { api.get("/boxes").then((r) => setBoxes(r.data)); }, []);
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 pt-10 space-y-12">
      <section className="grid md:grid-cols-[1fr_320px] gap-8 items-center">
        <div className="rise">
          <p className="label-eyebrow text-emerald-400 flex items-center gap-2"><Gift size={14} /> Mystery Box</p>
          <h1 className="font-display font-bold tracking-tighter text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.02]">Try your luck.<br /><span className="text-emerald-400">$2</span> a code.</h1>
          <p className="text-gray-400 mt-5 max-w-xl">Each blind box is packed with random Steam activation codes. Every code is worth more than $5, and some unlock games worth $50 to $100. Titles can repeat — what you get is down to luck.</p>
          <div className="flex flex-wrap gap-3 mt-6 text-sm">
            {["Every code worth $5+", "Jackpots up to $100", "Codes emailed to you"].map((t) => (
              <span key={t} className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-[#0F1117] border border-white/10"><Sparkles size={14} className="text-emerald-400" />{t}</span>
            ))}
          </div>
        </div>
        <img src={MYSTERY_IMG} alt="Mystery box" className="hidden md:block w-full rounded-xl border border-white/10 rise" style={{ animationDelay: "100ms" }} />
      </section>
      <section data-testid="mystery-box-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
        {!boxes ? Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-96 bg-white/5" />)
          : boxes.map((b, i) => <BoxCard key={b.id} box={b} index={i} />)}
      </section>
    </div>
  );
}
